import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Bike, MapPin } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast, PageHeader, Button, StatusBadge, EmptyState } from '../components/ui';

const orderStatusVariant = (status) => {
  switch (status) {
    case 'completed': return 'success';
    case 'served':    return 'success';
    case 'ready':     return 'primary';
    case 'preparing': return 'warning';
    case 'cancelled': return 'danger';
    default:          return 'neutral';
  }
};

const Riders = () => {
  const [riders, setRiders] = useState([]);
  const [myOrders, setMyOrders] = useState([]);
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (user?.role === 'rider') {
      fetchMyOrders();
    } else {
      fetchRiders();
    }
  }, [user]);

  const fetchRiders = async () => {
    try {
      const res = await axios.get('/api/riders', { headers: { 'x-auth-token': localStorage.getItem('token') } });
      setRiders(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchMyOrders = async () => {
    try {
      const res = await axios.get('/api/riders/my-orders', { headers: { 'x-auth-token': localStorage.getItem('token') } });
      setMyOrders(res.data);
    } catch (err) { console.error(err); }
  };

  const updateLocation = async (orderId) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(async (position) => {
        try {
          await axios.put('/api/riders/location', {
            orderId,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }, { headers: { 'x-auth-token': localStorage.getItem('token') } });
          toast.success('Location updated');
        } catch (err) {
          toast.error('Error updating location');
        }
      }, () => toast.error('Could not get your location'));
    } else {
      toast.error('Location is not available on this device');
    }
  };

  return (
    <>
      <PageHeader
        title={user?.role === 'rider' ? 'My Deliveries' : 'Riders'}
        subtitle={user?.role === 'rider' ? 'Active delivery orders assigned to you.' : 'Manage delivery riders.'}
      />
      {user?.role === 'rider' ? (
        <div className="space-y-3">
          {myOrders.length === 0 && (
            <div className="bg-white rounded-lg border border-neutral-200 shadow-card">
              <EmptyState
                icon={Bike}
                title="No deliveries assigned"
                message="Deliveries assigned to you will show up here."
              />
            </div>
          )}
          {myOrders.map(order => (
            <div key={order._id} className="bg-white rounded-lg border border-neutral-200 shadow-card p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-neutral-900 font-mono text-sm">{order.orderNumber}</p>
                <div className="mt-1">
                  <StatusBadge variant={orderStatusVariant(order.status)} dot>{order.status}</StatusBadge>
                </div>
              </div>
              <Button
                variant="primary"
                leftIcon={<MapPin size={16} />}
                onClick={() => updateLocation(order._id)}
                className="sm:w-auto w-full"
              >
                Update Location
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-neutral-200 shadow-card overflow-x-auto">
          <table className="app-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
              </tr>
            </thead>
            <tbody>
              {riders.length === 0 && (
                <tr>
                  <td colSpan="3">
                    <EmptyState
                      icon={Bike}
                      title="No riders yet"
                      message="Create a user with the Rider role from User Management."
                    />
                  </td>
                </tr>
              )}
              {riders.map(rider => (
                <tr key={rider._id}>
                  <td className="font-medium">{rider.name}</td>
                  <td className="text-neutral-600">{rider.email}</td>
                  <td className="capitalize text-neutral-600">{rider.role}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default Riders;
